import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Claim, ClaimStatus, UpdateClaim } from "../../models/Claim";
import restClient from "../../services/clients/AnonymousRestClient";
import { ELVData, PostELVData } from "../../models/ELVData";
import { CountryAutoComplete } from "../../models/Autocomplete";
import { IBANValidationResult } from "../../models/ValidationResult";
import authRestClient from "../../services/clients/AuthRestClient";
import { PostAddress } from "../../models/Address";
import { AxiosError } from "axios";
import { BadRequestError, FieldError } from "../../models/Errors/BadRequest";

const PostBankDetails = () => {

    const { claimGuid } = useParams();
    const navigate = useNavigate();

    let elvDataInit: PostELVData = {
        claimId          : "",
        iban             : "",
        bic              : "",
        bankname         : "",
        accountHolderName: "",
        bankAddress: {
            street    : "",
            postalCode: "",
            city      : "",
            countryId : -1
        },
        accountHolderAddress: {
            street     : "",
            postalCode : "",
            city       : "",
            countryId  : -1
        }
    };

    /* State Properties */
    const [claim, setClaim]             = useState<Claim>();
    const [confirmed, setConfirmed]     = useState<boolean>(false);

    const [elvData, setElvData]     = useState<PostELVData>(elvDataInit);
    const [countries, setCountries] = useState<CountryAutoComplete[]>([]);

    const [error, setError]             = useState<string>("");
    const [fieldErrors, setFieldErrors] = useState<FieldError>();
    const [sepa,  setSepa]              = useState<boolean>(false);

    /* Optional depending on sepa state */
    const [bankAddress, setBankAddress] = useState<PostAddress>({
        street     : "",
        postalCode : "",
        city       : "",
        countryId  : -1
    });

    /**
     * Method that handles form submit. Sends form data via axios instance to api endpoint to ad 
     * ELV Data for a specific claim form.
     * @param e
     * @returns
     */
    async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<ELVData | null>
    {
        /* Prevent default behaviour of HTMLFormElement that would refresh the page */
        e.preventDefault();

        let newElvData: ELVData | null = null;

        if (!confirmed)
        {
            setError("Please indicate that you have read and agree to the Terms and Conditions");
            return null;
        }

        /* Set Bankaddress details for posting to backend */
        elvData.bankAddress = sepa ? null : bankAddress;

        authRestClient.post<ELVData>("/api/elvdata", elvData)
            .then(response => {
                if (response.status === 200) {

                    newElvData = response.data;
                    /* Update Claim to "Confirmed" */
                    let payload: UpdateClaim =
                    {
                        guid      : elvData.claimId,
                        confirmed : confirmed,
                        status    : ClaimStatus.Requested
                    }

                    setError(""); // Reset Error view when post has been successfully transmitted ...

                    restClient.put<Claim>("/api/travelclaim", payload)
                        .then(response => {
                            /* OnSuccess redirect to Detail view for current Claim  */
                            navigate("/me/claims/" + claimGuid);
                        })
                        .catch(err => {
                           /* TODO: Proper Error Handling */
                        });
                }
            })
            .catch((err: AxiosError) =>
            {
                if (err.response!.status === 422) {
                    if (err.response?.data && (err.response.data as string)) {
                        setError(err.response.data as string)
                    }
                }
                else if (err.response!.status === 400) {
                    /* Bad Request | Most likley caused by missing required field */
                    if (err.response?.data && (err.response.data as BadRequestError))
                    {
                        let errors = (err.response.data as BadRequestError).errors;
                        setFieldErrors(errors);
                    }
                }
            });

        return newElvData;
    }

    /**
     * Method that renders an error message when an incorrect IBAN has been entered. (OnBlur)
     * @param e
     */
    async function validateIBAN(e: React.FocusEvent<HTMLInputElement, Element>): Promise<void>
    {
        restClient.get<IBANValidationResult>("/api/elvdata/validate/" + elvData.iban)
            .then(response => {
                if (response.status === 200)
                {
                    setError(response.data.isValid ? "" : response.data.errorMessage);
                    setSepa(response.data.isValid  ? response.data.sepa : false);
                }
            })
            .catch(err => { // Some generic error handling ...
                setSepa(false);
            });
    }

    /**
     * Functions renders HTML Form Element for submitting elvdata.
     * @returns JSX Element
     */
    function renderHTMLForm(): JSX.Element {
        return (
            <form onSubmit={handleSubmit}>

                <div className="row">

                    <div className="form-group col-12">
                        <label htmlFor="iban" className="fw-bold">IBAN</label>
                        <input id="iban" name="iban" className="form-control" onChange={e => setElvData(x => ({ ...x, iban: e.target.value }))} onBlur={validateIBAN} value={elvData.iban} />
                        <span className="text-danger">{getErrorMessageforField("IBAN")}</span>
                    </div>
                    <div className="form-group col-12">
                        <label htmlFor="bic" className="fw-bold">BIC</label>
                        <input id="bic" name="bic" className="form-control" onChange={e => setElvData(x => ({ ...x, bic: e.target.value }))} value={elvData.bic} />
                        <span className="text-danger">{getErrorMessageforField("BIC")}</span>
                    </div>

                    <hr />

                    { /* Bank Address Data */}

                    <div className="form-group col-12">
                        <label htmlFor="bankname" className="fw-bold">Bank Name</label>
                        <input id="bankname" name="bankname" className="form-control" onChange={e => setElvData(x => ({ ...x, bankname: e.target.value }))} value={elvData.bankname} />
                        <span className="text-danger">{getErrorMessageforField("Bankname")}</span>
                    </div>

                    {!sepa && (
                        <>
                            <div className="form-group col-12">
                                <label htmlFor="bankStreet" className="fw-bold">Street</label>
                                <input id="bankStreet" name="bankStreet" className="form-control" onChange={e => setBankAddress(x => ({ ...x, street: e.target.value }))} value={bankAddress.street} />
                                <span className="text-danger">{getErrorMessageforField("BankAddress.Street")}</span>
                            </div>
                            <div className="form-group col-12 col-md-4">
                                <label htmlFor="bankPostalCode" className="fw-bold">Postal Code</label>
                                <input type="text" name="bankPostalCode" id="bankPostalCode" className="form-control" onChange={e => setBankAddress(x => ({ ...x, postalCode: e.target.value }))} value={bankAddress.postalCode} />
                                <span className="text-danger">{getErrorMessageforField("BankAddress.PostalCode")}</span>
                            </div>
                            <div className="form-group col-12 col-md-4">
                                <label htmlFor="bankCity" className="fw-bold">City</label>
                                <input type="text" name="bankCity" className="form-control" onChange={e => setBankAddress(x => ({ ...x, city: e.target.value }))} value={bankAddress.city} />
                                <span className="text-danger">{getErrorMessageforField("BankAddress.City")}</span>
                            </div>
                            <div className="form-group col-12 col-md-4">
                                <label htmlFor="bankCountry" className="fw-bold">Country</label>
                                <select id="bankCountry" name="bankCountry" className="form-control" onChange={e => setBankAddress(x => ({ ...x, countryId: Number(e.target.value) }))} value={bankAddress.countryId} defaultValue={bankAddress.countryId}>
                                    <option key={"countryBank_None"} disabled value={-1}>Please select ...</option>
                                    {
                                        countries.map(entry => {
                                            return (
                                                <option key={"countryBank" + entry.value} value={entry.value}>
                                                    {entry.displayValue}
                                                </option>
                                            )
                                        })
                                    }
                                </select>
                                <span className="text-danger">{getErrorMessageforField("BankAddress.Country")}</span>
                            </div>
                        </>
                    )}

                    <hr />

                    { /* Account Holder Address Data */}

                    <div className="form-group col-12">
                        <label htmlFor="accHolderName" className="fw-bold">Account Holder</label>
                        <input type="text" id="accHolderName" name="accHolderName" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderName: e.target.value }))} value={elvData.accountHolderName} />
                        <span className="text-danger">{getErrorMessageforField("AccountHolderName")}</span>
                    </div>

                    <div className="form-group col-12">
                        <label htmlFor="accHolderBankStreet" className="fw-bold">Street</label>
                        <input type="text" id="accHolderBankStreet" name="accHolderBankStreet" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderAddress: { ...x.accountHolderAddress, street: e.target.value } }))} value={elvData.accountHolderAddress.street} />
                        <span className="text-danger">{getErrorMessageforField("AccountHolderAddress.Street")}</span>
                    </div>
                    <div className="form-group col-12 col-md-4">
                        <label htmlFor="acHolderPostalCode" className="fw-bold">Postal Code</label>
                        <input type="text" name="acHolderPostalCode" id="acHolderPostalCode" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderAddress: { ...x.accountHolderAddress, postalCode: e.target.value } }))} value={elvData.accountHolderAddress.postalCode} />
                        <span className="text-danger">{getErrorMessageforField("AccountHolderAddress.PostalCode")}</span>
                    </div>
                    <div className="form-group col-12 col-md-4">
                        <label htmlFor="accHolderCity" className="fw-bold">City</label>
                        <input type="text" id="accHolderCity" name="accHolderCity" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderAddress: { ...x.accountHolderAddress, city: e.target.value } }))} value={elvData.accountHolderAddress.city} />
                        <span className="text-danger">{getErrorMessageforField("AccountHolderAddress.City")}</span>
                    </div>
                    <div className="form-group col-12 col-md-4">
                        <label htmlFor="AccHolderBankCountry" className="fw-bold">Country</label>
                        <select id="AccHolderBankCountry" name="AccHolderBankCountry" className="form-control" onChange={e => setElvData(x => ({ ...x, accountHolderAddress: { ...x.accountHolderAddress, countryId: Number(e.target.value) } }))} defaultValue={elvData.accountHolderAddress.countryId}>
                            <option key={"countryAccHolder_None"} value={-1} disabled>Please select ...</option>
                            {
                                countries.map(entry => {
                                    return (
                                        <option key={"countryAccHolder" + entry.value} value={entry.value}>
                                            {entry.displayValue}
                                        </option>
                                    )
                                })
                            }
                        </select>
                        <span className="text-danger">{getErrorMessageforField("AccountHolderAddress.Country")}</span>
                    </div>

                    <hr />

                    <div className="input-group mb-3">
                        <div className="input-group-text">
                            <input className="form-check-input mt-0" type="checkbox" checked={confirmed} aria-label="Checkbox for following text input" onChange={e => setConfirmed(e.target.checked)} />
                        </div>
                        <span className="form-control">
                            I hereby confirm that I have read and accept <a target="_blank" rel="noreferrer" href="https://www.era.int/?100305&en">ERA's General Terms and Conditions</a> and that the information given is correct.
                        </span>
                    </div>

                    <hr />

                    <div className="form-group col-12">
                        <button type="submit" className="btn btn-primary w-100">Submit</button>
                    </div>

                </div>

            </form>
        )
    }

    /**
     * Functions renders Preview JSX Element for displaying previoulsy entered elvdata.
     * @returns JSX Element
     */
    function renderHTMLView(): JSX.Element {
        return (
            <div className="row">
                <div className="col-12">
                    
                </div>
            </div>
        )
    }

    /**
     * Method look up error messages for specified Field-Name and returns 
     * error message to method call.
     * @param fieldName: Name of the field
     * @returns: Error message to render
     */
    function getErrorMessageforField(fieldName: string): string {

        if (fieldErrors && fieldName in fieldErrors) // Check if FieldName exists within errorObject | Fetch error message ...
        {
            return fieldErrors[fieldName].at(0) ?? "";
        }
        return ""; // Default Return value in case no error has been found ....
    }

    useEffect(() => {
        /* Fetch data for the specified claimGUID from the api */
        authRestClient.get<Claim>("/api/travelclaim/" + claimGuid)
            .then(response => {
                if (response.status === 200)
                {
                    setClaim(response.data);
                    elvData.claimId = response.data.guid;
                    /* Load country autocomplete */
                    restClient.get<CountryAutoComplete[]>("/api/countries/autocomplete")
                        .then(response2 => {
                            setCountries(response2.data);
                        })
                        .catch(err => { 
                        })
                }
            })
            .catch(err => { 
            })
    }, [])

    /* JSX Element that represents the current page */
    return (
        <main className="container text-center mb-5">

            <div className="row">
                <div className="col-12">
                    <img src="/brand.png" alt="brand" className="brand" />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <h2>Account Information</h2>
                </div>
                <div className="col-12">
                    <p>Form to enter the account information to transfer the approved travel expenses.</p>
                </div>
            </div>

            {(error !== "") && (
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-danger fw-bold">
                            {error}
                        </div>
                    </div>
                </div>
            )}

            <div className="row">

                <div className="col-12 col-md-7 order-2 order-md-1">
                    <div className="card">
                        <div className="card-header fw-bold text-muted">
                            Account Information
                        </div>
                        <div className="card-body text-start">

                            {claim?.status === ClaimStatus.Saved ? renderHTMLForm() : renderHTMLView()}

                        </div>
                    </div>
                </div>

                { /* Show previously entered travel costs */ }
                <div className="col-12 col-md-5 order-1 order-md-2">
                    <div className="card">
                        <div className="card-header fw-bold text-muted">
                            Unit Costs
                        </div>
                        <div className="card-body text-start">

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-arrow-right me-2"></i>Your place of employment</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.institution?.name} - {claim?.institution?.address.street} - {claim?.institution?.address.city}
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-arrow-left me-2"></i>Event Location</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.event?.location}
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-map me-2"></i>Distance</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {Math.round(claim?.calculatedTravelCosts?.distance ?? 0)} km
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className={claim?.calculatedTravelCosts?.travelType === 0 ? "bi bi-airplane me-2" : "bi bi-train-front me-2"}></i>Transportation Type</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.calculatedTravelCosts?.travelType === 0 ? "Plane or combined" : "Rail Travel"}
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label>Travel contribution</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    &euro; {claim?.calculatedTravelCosts?.amount} 
                                </dd>
                            </dl>

                            <hr />

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-house me-2"></i>Nights Spent</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.calculatedAccommodationCosts?.nights ?? 0}
                                </dd>
                            </dl>
                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label>Amount</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    &euro; {claim?.calculatedAccommodationCosts?.amount ?? 0}
                                </dd>
                            </dl>

                        </div>
                    </div>
                </div>

            </div>

        </main>
    )
}
export default PostBankDetails;